import React, { useState } from 'react';
import { Mail, Send, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = '/api';

// Template types for quick actions
const QUICK_TEMPLATES = {
  rejection: {
    label: 'Rejection',
    description: 'Send a professional rejection email',
    defaultSubject: 'Application Update',
  },
  shortlist: {
    label: 'Shortlisted',
    description: 'Notify candidate they have been shortlisted',
    defaultSubject: 'Great News - You\'ve Been Shortlisted',
  },
  interview_invitation: {
    label: 'Interview Invitation',
    description: 'Invite candidate for an interview',
    defaultSubject: 'Interview Invitation',
    requiresDetails: true,
  },
  follow_up: {
    label: 'Follow-up',
    description: 'Send a follow-up message',
    defaultSubject: 'Follow-up',
  },
  next_steps: {
    label: 'Next Steps',
    description: 'Inform candidate about next steps',
    defaultSubject: 'Next Steps in Your Application',
  },
};

export const SendEmailDialog = ({
  open,
  onOpenChange,
  recipientName,
  recipientEmail,
  recordType,
  recordId,
  jobTitle = '',
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [templateType, setTemplateType] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [interviewDetails, setInterviewDetails] = useState('');

  const handleTemplateChange = (value) => {
    // Handle "custom" as empty/no template
    const actualValue = value === 'custom' ? '' : value;
    setTemplateType(actualValue);
    const template = QUICK_TEMPLATES[actualValue];
    if (template) {
      const subj = template.defaultSubject + (jobTitle ? ` - ${jobTitle}` : '');
      setSubject(subj);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use quick action endpoint for predefined templates
      if (templateType && QUICK_TEMPLATES[templateType]) {
        const response = await fetch(`${API}/admin/applications/${recordId}/quick-action`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            record_type: recordType,
            record_id: recordId,
            template_type: templateType,
            custom_message: message,
            additional_data: templateType === 'interview_invitation' ? {
              interview_details: interviewDetails || 'Our team will contact you to schedule the interview.',
            } : null,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Failed to send');
        }

        const result = await response.json();
        toast.success(
          result.email_status === 'mocked' 
            ? 'Email logged (test mode)' 
            : 'Email sent successfully'
        );
        
        if (result.stage_updated_to) {
          toast.info(`Application status updated to: ${result.stage_updated_to}`);
        }
      } else {
        // Use general communication endpoint
        const response = await fetch(`${API}/admin/communications/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            record_type: recordType,
            record_id: recordId,
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            subject: subject || 'Message from WeDo HR Services',
            message: message.replace(/\n/g, '<br>'),
            communication_type: 'general',
            send_email: sendEmail,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Failed to send');
        }

        const result = await response.json();
        toast.success(
          result.status === 'mocked' 
            ? 'Communication logged (test mode)' 
            : sendEmail ? 'Email sent successfully' : 'Note saved'
        );
      }

      onOpenChange(false);
      if (onSuccess) onSuccess();
      
      // Reset form
      setTemplateType('');
      setSubject('');
      setMessage('');
      setInterviewDetails('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Communication
          </DialogTitle>
          <DialogDescription>
            Send an email to {recipientName} ({recipientEmail})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick Template Selection */}
          <div>
            <Label>Quick Template (Optional)</Label>
            <Select value={templateType || 'custom'} onValueChange={handleTemplateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template or write custom message" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom Message</SelectItem>
                {Object.entries(QUICK_TEMPLATES).map(([key, template]) => (
                  <SelectItem key={key} value={key}>
                    {template.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templateType && QUICK_TEMPLATES[templateType] && (
              <p className="text-sm text-gray-500 mt-1">
                {QUICK_TEMPLATES[templateType].description}
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          {/* Interview Details (for interview invitation) */}
          {templateType === 'interview_invitation' && (
            <div>
              <Label htmlFor="interviewDetails">Interview Details</Label>
              <Textarea
                id="interviewDetails"
                value={interviewDetails}
                onChange={(e) => setInterviewDetails(e.target.value)}
                placeholder="Date, time, location, format (in-person/video), etc."
                rows={3}
              />
            </div>
          )}

          {/* Message */}
          <div>
            <Label htmlFor="message">
              {templateType ? 'Additional Message (Optional)' : 'Message'}
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                templateType 
                  ? 'Add any personalized message...' 
                  : 'Write your message here...'
              }
              rows={5}
            />
          </div>

          {/* Send Email Toggle */}
          {!templateType && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendEmail"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
              />
              <Label htmlFor="sendEmail" className="text-sm font-normal">
                Send email to recipient (uncheck to save as internal note only)
              </Label>
            </div>
          )}

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              {sendEmail 
                ? 'Email is currently in test mode. Messages are logged but not actually sent until email service is activated.'
                : 'This will be saved as an internal note and not sent to the recipient.'
              }
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={loading}>
            {loading ? (
              'Sending...'
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {sendEmail ? 'Send Email' : 'Save Note'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Screening Note Dialog
export const ScreeningNoteDialog = ({
  open,
  onOpenChange,
  applicationId,
  candidateName,
  candidateEmail,
  jobTitle,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [personalizedMessage, setPersonalizedMessage] = useState('');
  const [sendToCandidate, setSendToCandidate] = useState(false);

  const handleSave = async () => {
    if (!note.trim()) {
      toast.error('Please enter a screening note');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/applications/${applicationId}/screening-note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: applicationId,
          note: note,
          send_to_candidate: sendToCandidate,
          personalized_message: sendToCandidate ? personalizedMessage : null,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save');
      }

      const result = await response.json();
      toast.success(
        sendToCandidate 
          ? `Screening note saved and email ${result.email_status === 'mocked' ? 'logged (test mode)' : 'sent'}`
          : 'Screening note saved'
      );

      onOpenChange(false);
      if (onSuccess) onSuccess();
      
      // Reset form
      setNote('');
      setPersonalizedMessage('');
      setSendToCandidate(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Add Screening Note
          </DialogTitle>
          <DialogDescription>
            Add a screening note for {candidateName}'s application
            {jobTitle && ` to ${jobTitle}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Screening Note */}
          <div>
            <Label htmlFor="note">Screening Note</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Candidate lacks required experience in X, Y, Z..."
              rows={4}
            />
            <p className="text-sm text-gray-500 mt-1">
              This note will be saved to the application record.
            </p>
          </div>

          {/* Send to Candidate Toggle */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sendToCandidate"
              checked={sendToCandidate}
              onCheckedChange={setSendToCandidate}
            />
            <Label htmlFor="sendToCandidate" className="text-sm font-normal">
              Send rejection/update email to candidate
            </Label>
          </div>

          {/* Personalized Message (only if sending) */}
          {sendToCandidate && (
            <div>
              <Label htmlFor="personalizedMessage">Personalized Message (Optional)</Label>
              <Textarea
                id="personalizedMessage"
                value={personalizedMessage}
                onChange={(e) => setPersonalizedMessage(e.target.value)}
                placeholder="Add a personalized message to include in the email..."
                rows={3}
              />
              <p className="text-sm text-gray-500 mt-1">
                This will be included in the rejection email. The screening note itself 
                is internal and won't be shared.
              </p>
            </div>
          )}

          {/* Info */}
          {sendToCandidate && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                A professional rejection email will be sent to {candidateEmail}. 
                The internal screening note will NOT be included in the email.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              'Saving...'
            ) : (
              <>
                {sendToCandidate ? (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Save & Send Email
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Note
                  </>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendEmailDialog;
